/* eslint-disable max-statements */
/* eslint-disable init-declarations */
/* eslint-disable max-lines-per-function */
import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Kitchen3dController as TdComponentController } from '@rta/rta-blueprint-3d-component';
import { Box, Button, Flex, FormLabel, Icon, IconButton, Input, Select, Spinner, Stack, Text, Tooltip, useToast } from '@chakra-ui/react';
import { SurroundMaterial, useSubscription, type KitchenTrimProfile } from '@rta/rta-blueprint-library';
import { useKeystoneSession } from '../../../auth/use-keystone-session';
import { useCallback, useContext, useMemo, useState, type JSX } from 'react';

import { ContextPanelContent } from './context-panel-content';
import { displayDropdowns } from './helpers';
import { OutdoorPhotosPicker } from '@components/ui';
import { AppContext, DesignActionTypes, type Controller, type DesignState } from '@context';
import { lang } from '@lang';
import { projectState, selectIsFloorLocked, selectIsSkyLocked, selectNavigation, setFloorLocked, setSkyLocked, useAppDispatch, useAppSelector } from '@state';
import { saveCustomerBlueprint } from '@external';
import { useDesign } from '@hooks';

export const KitchenPropertiesPanel = (): JSX.Element => {
	const [controller] = useDesign<BpController | TdComponentController>();
	const [customTexture, setCustomTexture] = useState<string | null>(null);
	const [isDragOver, setIsDragOver] = useState(false);
	const [isTextureQualityLoading, setIsTextureQualityLoading] = useState(false);
	const { appState: { customerDesigns }, appDispatch } = useContext(AppContext);
	const dispatch = useAppDispatch();
	const { getToken } = useKeystoneSession();
	const toast = useToast();
	const isSkyLocked = useAppSelector(selectIsSkyLocked);
	const isFloorLocked = useAppSelector(selectIsFloorLocked);
	const appNav = useAppSelector(selectNavigation);
	const project = useAppSelector(projectState);
	const kitchenDealId = controller?.kitchen?.dealId ?? '';
	const deal = project.find((d) => d.dealId === kitchenDealId) ?? project?.[0];
	const dealId = deal?.dealId ?? null;
	const readonly = controller?.readonly ?? true;

	const saveDesign = useCallback(async (design: DesignState): Promise<[string | null, string | null]> => {
		const { bpController, blueprint } = design;
		const k = bpController?.kitchen ?? null;
		if (!bpController || !k) return [null, null];
		const { email: customerEmail } = appNav;
		if (!customerEmail) return [null, 'Missing customer email'];
		const kitchenDataOverride = k.version !== null
			? { ...k.serialize(), version: null, baseVersion: k.version }
			: undefined;
		const result = await saveCustomerBlueprint({
			controller: bpController as unknown as Controller,
			customerEmail,
			mode: 'live',
			getToken,
			kitchenDataOverride
		});
		const [, saveErr] = result;
		if (!saveErr) {
			k.isLocal = false;
			appDispatch({ type: DesignActionTypes.setIsDirty, payload: { id: blueprint.id, isDirty: false }});
		}
		return result;
	}, [appDispatch, appNav, getToken]);

	const syncSkyToAll = useCallback(async (value: string): Promise<void> => {
		const saves = customerDesigns.designs.map(async (design) => {
			const td = design.tdController;
			if (td) td.skyKey = value;
			return saveDesign(design);
		});
		const results = await Promise.all(saves);
		if (results.some(([, err]) => err !== null)) {
			toast({ title: 'Could not save the sky to all designs', status: 'error', isClosable: true });
		}
	}, [customerDesigns.designs, saveDesign, toast]);

	const syncFloorToAll = useCallback(async (value: string): Promise<void> => {
		const saves = customerDesigns.designs.map(async (design) => {
			const td = design.tdController;
			if (td) td.floorKey = value;
			return saveDesign(design);
		});
		const results = await Promise.all(saves);
		if (results.some(([, err]) => err !== null)) {
			toast({ title: 'Could not save the floor to all designs', status: 'error', isClosable: true });
		}
	}, [customerDesigns.designs, saveDesign, toast]);

	useSubscription(controller?.kitchen ?? null, false, [
		['kitchen_fuel', '*'],
		['surround_material', '*'],
		['island_size', '*'],
		['panel_size', '*'],
		['panel_trim', '*'],
		['panel_trims', '*']
	]);

	useSubscription(controller ?? null, false, [
		['sky_key', '*'],
		['floor_key', '*'],
		['custom_texture', '*'],
		['texture_quality', '*']
	]);

	const applyCustomTexture = useCallback((dataUrl: string) => {
		const img = new Image();
		img.onload = () => {
			const viewportWidth = window.innerWidth;
			const viewportHeight = window.innerHeight;

			const imageAspect = img.width / img.height;
			const viewportAspect = viewportWidth / viewportHeight;

			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			if (!ctx) return;

			canvas.width = viewportWidth;
			canvas.height = viewportHeight;

			ctx.fillStyle = 'white';
			ctx.fillRect(0, 0, viewportWidth, viewportHeight);

			let drawWidth, drawHeight, drawX, drawY;

			if (imageAspect > viewportAspect) {
				drawWidth = viewportWidth;
				drawHeight = viewportWidth / imageAspect;
				drawX = 0;
				drawY = (viewportHeight - drawHeight) / 2;
			} else {
				drawWidth = viewportHeight * imageAspect;
				drawHeight = viewportHeight;
				drawX = (viewportWidth - drawWidth) / 2;
				drawY = 0;
			}

			ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

			const letterboxedDataUrl = canvas.toDataURL('image/jpeg', 0.9);

			setCustomTexture(letterboxedDataUrl);
			const c = controller as TdComponentController;

			if (c.setCustomTexture) {
				c.setCustomTexture(letterboxedDataUrl);
			}

			if (c.skyKey) {
				c.skyKey = 'sky/custom';
			}
		};

		img.src = dataUrl;
	}, [controller, setCustomTexture]);

	const handleFileUpload = useCallback((file: File) => {
		if (file) {
			const reader = new FileReader();
			reader.onload = (e) => {
				const dataUrl = e.target?.result as string;
				applyCustomTexture(dataUrl);
			};
			reader.readAsDataURL(file);
		}
	}, [applyCustomTexture]);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
	}, []);

	const handleDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
		const { files } = e.dataTransfer;
		if (files.length > 0) {
			handleFileUpload(files[0]);
		}
	}, [handleFileUpload]);

	const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const { files } = e.target;
		if (files && files.length > 0) {
			handleFileUpload(files[0]);
		}
	}, [handleFileUpload]);

	// Detect whether any panels have thicknesses that don't match the current surround material's defaults
	const panelThicknessMismatch = useMemo((): boolean => {
		if (!controller) return false;
		const { material: { surround: s } } = controller.kitchen;
		if (!s || !s.hasPerRoleThickness) return false;

		const islands = controller.kitchen.design?.islands?.children ?? [];
		for (const island of islands) {
			for (const panel of island.panels.children) {
				const [enabled, role] = (() => {
					if (panel.isFrontPanel) return [true, 'front' as const];
					if (panel.isBackPanel) return [true, 'back' as const];
					if (panel.isLeftPanel) return [true, 'left' as const];
					if (panel.isRightPanel) return [true, 'right' as const];
					return [false, 'front' as const];
				})();

				if (!enabled) continue;

				const expectedThickness = s.getThicknessForRole(role);
				if (Math.abs(panel.thickness - expectedThickness) > 0.001) {
					return true;
				}
			}
		}
		return false;
	}, [controller]);

	const handleApplyDefaultThickness = useCallback((): void => {
		if (!controller) return;
		const islands = controller.kitchen.design?.islands?.children ?? [];
		for (const island of islands) {
			const { surround } = island;
			// Explicitly set each panel to the current surround's per-role thickness
			island.panels.children.forEach((panel) => {
				const role = panel.isFrontPanel ? 'front' : panel.isBackPanel ? 'back' : panel.isLeftPanel ? 'left' : 'right';
				panel.size = { ...panel.size, depth: surround.getThicknessForRole(role) };
			});
			// Re-trigger size calculation to update side panel widths
			island.size = island.size;
		}
	}, [controller]);

	if (!controller) return <></>;
	const { fuelchoice, trimProfile, material: { surround }} = controller.kitchen!;
	const { skyKey = '', floorKey = '' } = controller as TdComponentController;
	const tdController = controller as TdComponentController;
	const supportsTextureQuality = typeof tdController.setTextureQuality === 'function';
	const currentTextureQuality = tdController.textureQuality ?? 2;

	const handleTextureQualityChange = async (e: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
		const next = Number(e.target.value);
		if (!Number.isFinite(next)) return;
		setIsTextureQualityLoading(true);
		try {
			await tdController.setTextureQuality(next);
		} finally {
			setIsTextureQualityLoading(false);
		}
	};

	return <ContextPanelContent title={lang.contextPanels.title.kitchenProperties}>
		<Stack mt="5px" spacing="2" width="100%" height="65%">
			<Box w="100%">
				<FormLabel htmlFor="fuel" fontSize="small">Surround:</FormLabel>
				<Select id="fuel" fontSize="small"
					value={surround?.key}
					disabled={readonly}
					onChange={ (e) => {
						if (surround) {
							const key = e.target.value;
							controller.kitchen?.material.setSurround(key);
							// Disable corner trims for slat and MOK surrounds (no corner pieces)
							const hasCorners = !key.startsWith('slat') && !key.startsWith('moks/');
							controller.options.corner.show = hasCorners;
						}
					}}>
					{displayDropdowns(SurroundMaterial.assetData)}
				</Select>
				{panelThicknessMismatch && !readonly && (
					<Tooltip label="Update panel thicknesses to match current surround material defaults" fontSize="xs">
						<Button
							size="xs"
							mt="2"
							colorScheme="blue"
							variant="outline"
							onClick={handleApplyDefaultThickness}
						>
							Apply Default Panel Thickness
						</Button>
					</Tooltip>
				)}
			</Box>
			<Box>
				<FormLabel htmlFor="trim" fontSize="small">Trim:</FormLabel>
				<Select id="trim" fontSize="small"
					value={trimProfile}
					disabled={readonly}
					onChange={ (e) => {
						controller.kitchen!.trimProfile = e.target.value as KitchenTrimProfile;
					}}>
					<option value="standard">Standard</option>
					<option value="flat">Flat</option>
					{trimProfile === 'mixed' && <option value="mixed" disabled>Mixed (per corner)</option>}
				</Select>
			</Box>
			<Box>
				<FormLabel htmlFor="fuel" fontSize="small">Fuel:</FormLabel>
				<Select id="fuel" fontSize="small"
					value={fuelchoice ?? 'ng'}
					disabled={readonly}
					onChange={ (e) => {
						controller.kitchen!.fuelchoice = e.target.value;
					}}>
					<option value="ng">NG</option>
					<option value="lp-tank">LP Tank</option>
					<option value="lp-inline">LP Inline</option>
					<option value="none">None</option>
				</Select>
			</Box>
			<Box>
				<Flex align="center" justify="space-between">
					<FormLabel htmlFor="sky" fontSize="small" mb="0">Sky:</FormLabel>
					<Tooltip label={isSkyLocked ? 'Unlock sky across designs' : 'Lock sky across designs'} fontSize="xs">
						<IconButton
							aria-label={isSkyLocked ? 'Unlock sky' : 'Lock sky'}
							size="xs"
							variant="ghost"
							color={isSkyLocked ? 'blue.500' : 'gray.400'}
							onClick={() => {
								const next = !isSkyLocked;
								dispatch(setSkyLocked(next));
								if (next && skyKey) {
									void syncSkyToAll(skyKey);
								}
							}}
							icon={
								isSkyLocked
									? <Icon viewBox="0 0 24 24" boxSize="14px"><path fill="currentColor" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/></Icon>
									: <Icon viewBox="0 0 24 24" boxSize="14px"><path fill="currentColor" d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z"/></Icon>
							}
						/>
					</Tooltip>
				</Flex>
				<Select id="sky" fontSize="small"
					value={skyKey}
					disabled={readonly || !(controller as TdComponentController).skyKey}
					onChange={ (e) => {
						const c = controller as TdComponentController;
						if (c.skyKey) {
							const selectedSky = e.target.value;
							c.skyKey = selectedSky;

							if (isSkyLocked) {
								void syncSkyToAll(selectedSky);
							}

							// Clear custom texture when switching away from custom sky
							if (selectedSky !== 'sky/custom') {
								setCustomTexture(null);
								// Clear the custom texture in the 3D component as well
								if (c.setCustomTexture) {
									c.setCustomTexture('');
								}
							}
						}
					}}>
					{displayDropdowns({
						'background': { enabled: true, displayName: 'Default' },
						'sky/overcast': { enabled: true, displayName: 'Overcast' },
						'sky/pure-sky': { enabled: true, displayName: 'Pure Sky' },
						'sky/sunrise': { enabled: true, displayName: 'Sunrise' },
						'sky/beach': { enabled: true, displayName: 'Beach' },
						'sky/under_tower': { enabled: true, displayName: 'Under Tower' },
						'sky/qwantani_patio': { enabled: true, displayName: 'Qwantani Patio' },
						'sky/rainforest_trail': { enabled: true, displayName: 'Rainforest Trail' },
						'sky/studio_garden': { enabled: true, displayName: 'Studio Garden' },
						'sky/little_paris': { enabled: true, displayName: 'Little Paris' },
						'sky/meadow': { enabled: true, displayName: 'Meadow' },
						'sky/symmetrical_garden': { enabled: true, displayName: 'Symmetrical Garden' },
						'sky/pretoria_gardens': { enabled: true, displayName: 'Pretoria Gardens' },
						'sky/ballawley_park': { enabled: true, displayName: 'Ballawley Park' },
						'sky/tiergarten': { enabled: true, displayName: 'Tiergarten' },
						'sky/custom': { enabled: true, displayName: 'Custom' }
					})}
				</Select>
				{supportsTextureQuality && (
					<Flex align="center" mt="1" gap="2">
						<Select
							id="sky-quality"
							size="xs"
							fontSize="x-small"
							width="auto"
							flex="0 0 auto"
							value={currentTextureQuality}
							disabled={readonly || isTextureQualityLoading || !tdController.skyKey}
							onChange={handleTextureQualityChange}
						>
							<option value={2}>Low (1K)</option>
							<option value={4}>Medium (2K)</option>
							<option value={9}>High (4K)</option>
							<option value={10}>Ultra (8K)</option>
						</Select>
						{isTextureQualityLoading
							? <Spinner size="xs" color="blue.500" />
							: <Box boxSize="12px" />}
						<Text fontSize="xx-small" color="gray.500">Sky quality</Text>
					</Flex>
				)}
			</Box>
			<Box>
				<Flex align="center" justify="space-between">
					<FormLabel htmlFor="floor" fontSize="small" mb="0">Floor:</FormLabel>
					<Tooltip label={isFloorLocked ? 'Unlock floor across designs' : 'Lock floor across designs'} fontSize="xs">
						<IconButton
							aria-label={isFloorLocked ? 'Unlock floor' : 'Lock floor'}
							size="xs"
							variant="ghost"
							color={isFloorLocked ? 'blue.500' : 'gray.400'}
							onClick={() => {
								const next = !isFloorLocked;
								dispatch(setFloorLocked(next));
								if (next && floorKey) {
									void syncFloorToAll(floorKey);
								}
							}}
							icon={
								isFloorLocked
									? <Icon viewBox="0 0 24 24" boxSize="14px"><path fill="currentColor" d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2z"/></Icon>
									: <Icon viewBox="0 0 24 24" boxSize="14px"><path fill="currentColor" d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z"/></Icon>
							}
						/>
					</Tooltip>
				</Flex>
				<Select id="floor" fontSize="small"
					value={floorKey}
					disabled={readonly || !(controller as TdComponentController).floorKey}
					onChange={ (e) => {
						const c = controller as TdComponentController;
						if (c.floorKey) {
							const selectedFloor = e.target.value;
							c.floorKey = selectedFloor;

							if (isFloorLocked) {
								void syncFloorToAll(selectedFloor);
							}
						}
					}}>
					{displayDropdowns({
						'floor/grid-tile': { enabled: true, displayName: 'Default' },
						'floor/asphalt-01': { enabled: true, displayName: 'Asphalt' },
						'floor/brick-moss-001': { enabled: true, displayName: 'Brick Moss' },
						'floor/brick-pavement-02': { enabled: true, displayName: 'Brick Pavement' },
						'floor/concrete-pavers': { enabled: true, displayName: 'Concrete Pavers' },
						'floor/diagonal-parquet': { enabled: true, displayName: 'Diagonal Parquet' },
						'floor/granite-tile': { enabled: true, displayName: 'Granite Tile' },
						'floor/gravel-embedded-concrete': { enabled: true, displayName: 'Gravel Embedded Concrete' },
						'floor/herringbone-parquet': { enabled: true, displayName: 'Herringbone Parquet' },
						'floor/herringbone-pavement': { enabled: true, displayName: 'Herringbone Pavement' },
						'floor/large-sandstone-blocks': { enabled: true, displayName: 'Large Sandstone Blocks' },
						'floor/old-wood-floor': { enabled: true, displayName: 'Old Wood Floor' },
						'floor/pavement-02': { enabled: true, displayName: 'Pavement 02' },
						'floor/pavement-03': { enabled: true, displayName: 'Pavement 03' },
						'floor/pavement-05': { enabled: true, displayName: 'Pavement 05' },
						'floor/patterned-cobblestone': { enabled: true, displayName: 'Patterned Cobblestone' },
						'floor/pebble-embedded-concrete': { enabled: true, displayName: 'Pebble Embedded Concrete' },
						'floor/sparse-grass': { enabled: true, displayName: 'Sparse Grass' },
						'floor/square-tiles': { enabled: true, displayName: 'Square Tiles' },
						'floor/stone-tiles': { enabled: true, displayName: 'Stone Tiles' },
						'floor/t-brick-floor': { enabled: true, displayName: 'T Brick Floor' },
						'floor/textured-beige': { enabled: true, displayName: 'Textured Beige' },
						'floor/weathered-brown-planks': { enabled: true, displayName: 'Weathered Brown Planks' },
						'floor/wood-floor': { enabled: true, displayName: 'Wood Floor' },
						'floor/wood-floor-deck': { enabled: true, displayName: 'Wood Floor Deck' },
						'floor/wood-planks-grey': { enabled: true, displayName: 'Wood Planks Grey' },


						'floor/hide': { enabled: true, displayName: 'Hide Floor' }
					})}
				</Select>
			</Box>
			<Box>
				<FormLabel htmlFor="customTexture" fontSize="small">Custom Sky Texture:</FormLabel>
				<Box
					border="2px dashed"
					borderColor={isDragOver ? 'blue.400' : 'gray.300'}
					borderRadius="md"
					p={4}
					textAlign="center"
					cursor="pointer"
					bg={isDragOver ? 'blue.50' : 'gray.50'}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					onClick={() => document.getElementById('fileInput')?.click()}
				>
					<Input
						id="fileInput"
						type="file"
						accept="image/*"
						disabled={readonly || !(controller as TdComponentController).skyKey}
						onChange={handleFileChange}
						style={{ display: 'none' }}
					/>
					{customTexture
						? (
							<Text fontSize="sm" color="green.600">
								Custom sky texture uploaded
							</Text>
						)
						: (readonly || !(controller as TdComponentController).skyKey)
							? (
								<Text fontSize="sm" color="gray.600">
									Only avaliable in 3D Viewer
								</Text>
							)
							: (
								<Text fontSize="sm" color="gray.600">
									Drag & drop a JPG or PNG file here or click to browse
								</Text>
							)}
				</Box>
				<OutdoorPhotosPicker dealId={dealId} onSelect={applyCustomTexture} />
			</Box>
		</Stack>
	</ContextPanelContent>;
};
